import { writable, derived, get } from "svelte/store";
import { browser } from "$app/environment";
import { page } from "$app/stores";
import type { Campaign, ValidationError } from "$lib/types";
import { CampaignAPI } from "$lib/api/campaign";

const CAMPAIGN_PREFIX = "campaign_";

function createCampaignStore() {
  const { subscribe, update } = writable<{ [id: string]: Campaign }>({});

  function saveCampaignToLocalStorage(state: Campaign) {
    if (!browser) return;
    try {
      localStorage.setItem(`${CAMPAIGN_PREFIX}${state.slug}`, JSON.stringify(state));
    } catch (error) {
      if (error instanceof DOMException && error.name === "QuotaExceededError") {
        handleStorageQuotaExceeded(state);
      }
    }
  }

  function handleStorageQuotaExceeded(state: Campaign) {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(CAMPAIGN_PREFIX))
      .sort((a, b) => {
        const timeA = getStorageItemTimestamp(a);
        const timeB = getStorageItemTimestamp(b);
        return timeA - timeB;
      })
      .some((key) => attemptToSaveAfterRemoval(key, state));
  }

  function getStorageItemTimestamp(key: string): number {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item).updatedAt || 0 : 0;
  }

  function attemptToSaveAfterRemoval(key: string, state: Campaign): boolean {
    localStorage.removeItem(key);
    try {
      localStorage.setItem(`${CAMPAIGN_PREFIX}${state.slug}`, JSON.stringify(state));
      return true;
    } catch {
      return false;
    }
  }

  return {
    subscribe,

    async setICPData(id: string, icpData: Campaign["icp"]) {
      logger.log("setICPData: Saving ICP data:", { id, icpData });
      update((campaigns) => {
        const campaign = campaigns[id];
        if (!campaign) {
          logger.log(`setICPData: Campaign not found for id: ${id}`);
          return campaigns;
        }
        const updatedCampaign = {
          ...campaign,
          icp: {
            ...campaign.icp,
            criteria: { ...campaign.icp?.criteria, ...icpData?.criteria },
            leads: icpData?.leads || campaign.icp?.leads || [],
          },
          updatedAt: new Date(),
        };
        logger.log("setICPData: Updated campaign ICP data:", updatedCampaign.icp);
        saveCampaignToLocalStorage(updatedCampaign);
        return {
          ...campaigns,
          [id]: updatedCampaign,
        };
      });
    },

    async setOfferData(id: string, offerData: Campaign["offer"]) {
      update((campaigns) => {
        const campaign = campaigns[id];
        if (!campaign) return campaigns;
        const updatedCampaign = {
          ...campaign,
          offer: offerData,
          updatedAt: new Date(),
        };
        saveCampaignToLocalStorage(updatedCampaign);
        return {
          ...campaigns,
          [id]: updatedCampaign,
        };
      });
    },

    async setOutreachData(id: string, outreachData: Campaign["outreach"]) {
      update((campaigns) => {
        const campaign = campaigns[id];
        if (!campaign) return campaigns;
        const updatedCampaign = {
          ...campaign,
          outreach: outreachData,
          updatedAt: new Date(),
        };
        saveCampaignToLocalStorage(updatedCampaign);
        return {
          ...campaigns,
          [id]: updatedCampaign,
        };
      });
    },

    async setSettings(id: string, settings: Campaign["settings"]) {
      update((campaigns) => {
        const campaign = campaigns[id];
        if (!campaign) return campaigns;
        const updatedCampaign = {
          ...campaign,
          settings,
          updatedAt: new Date(),
        };
        saveCampaignToLocalStorage(updatedCampaign);
        return {
          ...campaigns,
          [id]: updatedCampaign,
        };
      });
    },

    validateCampaign(id: string): ValidationError[] {
      const store = get({ subscribe });
      const campaign = store[id];
      const errors: ValidationError[] = [];
      if (!campaign) return [{ field: "campaign", message: "Campaign not found" }];
      if (!campaign.icp?.criteria?.jobTitle) {
        errors.push({ field: "jobTitle", message: "Job title is required", step: "icp" });
      }
      if (!campaign.offer?.sections?.length) {
        errors.push({ field: "sections", message: "At least one section is required", step: "offer" });
      }
      if (!campaign.outreach?.steps?.length) {
        errors.push({ field: "steps", message: "At least one step is required", step: "outreach" });
      }
      return errors;
    },

    async initializeCampaign(id: string, campaignData: Partial<Campaign>) {
      logger.log("Initializing campaign:", { id, campaignData });
      update((store) => {
        const existingCampaign = store[id];
        const newCampaign: Campaign = {
          id,
          createdAt: new Date(),
          updatedAt: new Date(),
          error: null,
          icp: {
            criteria: {},
            leads: [],
          },
          ...existingCampaign,
          ...campaignData,
        };
        logger.log("Initialized campaign:", newCampaign);
        saveCampaignToLocalStorage(newCampaign);
        return {
          ...store,
          [id]: newCampaign,
        };
      });
    },

    async loadCampaign(name: string, id: string, userID?: string) {
      try {
        const existingCampaign = this.getCampaign(id);
        if (existingCampaign) {
          logger.log("loadCampaign: Campaign exists:", existingCampaign);
          return;
        }

        const campaignData = await CampaignAPI.getCampaign(id);
        if (campaignData) {
          logger.log("loadCampaign: Got campaign from API:", campaignData);
          const mappedCampaignData = {
            ...campaignData,
            title: campaignData.campaign.title,
            status: campaignData.campaign.status,
          };
          await this.initializeCampaign(id, mappedCampaignData);
        } else {
          const savedCampaign = this.getCampaignFromLocalStorage(id);
          if (savedCampaign) {
            logger.log("loadCampaign: Loading from localStorage:", savedCampaign);
            await this.initializeCampaign(id, savedCampaign);
          } else {
            logger.log("loadCampaign: Creating new campaign:", { name, userID });
            await this.initializeCampaign(id, {
              title: name,
              userID,
              createdAt: new Date(),
              updatedAt: new Date(),
            });
          }
        }
      } catch (error) {
        logger.error("Error loading campaign:", error);
        this.setError(id, error instanceof Error ? error.message : "Failed to load campaign");
      }
    },

    setError(id: string, error: string) {
      update((store) => {
        const campaign = store[id];
        if (!campaign) return store;
        const updatedCampaign = {
          ...campaign,
          error,
        };
        saveCampaignToLocalStorage(updatedCampaign);
        return {
          ...store,
          [id]: updatedCampaign,
        };
      });
    },

    getCampaignFromLocalStorage(id: string): Campaign | null {
      if (!browser) return null;
      try {
        const savedCampaign = localStorage.getItem(`${CAMPAIGN_PREFIX}${id}`);
        return savedCampaign ? JSON.parse(savedCampaign) : null;
      } catch (error) {
        console.error("Error parsing saved campaign from localStorage:", error);
        return null;
      }
    },

    getCampaign(id: string): Campaign | undefined {
      return get({ subscribe })[id];
    },

    updateCampaign(id: string, data: Partial<Campaign>) {
      logger.log("Updating campaign:", { id, data });
      update((campaigns) => {
        if (campaigns[id]) {
          const updatedCampaign = {
            ...campaigns[id],
            ...data,
            updatedAt: new Date(),
          };
          if (browser) {
            try {
              logger.log("Saving updated campaign to localStorage:", updatedCampaign);
              localStorage.setItem(`${CAMPAIGN_PREFIX}${id}`, JSON.stringify(updatedCampaign));
            } catch (error) {
              console.error("Failed to save campaign to localStorage:", error);
            }
          }
          return {
            ...campaigns,
            [id]: updatedCampaign,
          };
        }
        return campaigns;
      });
    },
  };
}

export const campaignStore = createCampaignStore();

export const currentCampaignICP = derived([campaignStore, page], ([$campaigns, $page]) => {
  const slug = $page.params.slug;
  if (!slug) return undefined;
  const campaign = $campaigns[slug];
  logger.log("Current Campaign ICP:", { slug, icp: campaign?.icp });
  return campaign?.icp;
});

export const currentCampaignOffer = derived([campaignStore, page], ([$campaigns, $page]) =>
  $page.params.slug ? $campaigns[$page.params.slug]?.offer : undefined,
);

export const currentCampaignOutreach = derived([campaignStore, page], ([$campaigns, $page]) =>
  $page.params.slug ? $campaigns[$page.params.slug]?.outreach : undefined,
);

export const currentCampaignSettings = derived([campaignStore, page], ([$campaigns, $page]) =>
  $page.params.slug ? $campaigns[$page.params.slug]?.settings : undefined,
);
